import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, array, number, object, string } from 'yup'

import { useMetaResponseHandler } from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { TpvsContract } from '@/Store/application'
import { TpvModel, TpvsRepository } from '@/Store/domain'

import { removeFirstZeroFromTPV } from '../services/tpvServices'
import { tpvSchema } from '@/Shared/ui/form/validations/tpvSchema'
import { MetaStatusCodes } from '@/Shared/domain/enum/MetaStatusCodes'
import { dataToExcel } from '@/Shared/domain'
import { storesTranslation } from '../storesTranslation'

export interface TpvForm {
  terminalMid: string
  merchantTargetId: number
  merchantTargetList?: number[]
  multiFieldFileter?: string
}

export interface TpvFilters {
  multiFieldFileter: string
  merchantTargetList: number[]
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<TpvForm> =>
  object({
    terminalMid: tpvSchema(t).required(),
    merchantTargetId: number().required(),
    merchantTargetList: array(number().required()).optional(),
    multiFieldFileter: string().optional(),
  })

export interface IUseTpvController {
  form: UseFormReturn<TpvForm>
  model: TpvModel[]
  count: number
  addNewTpv: (data: TpvForm) => Promise<boolean>
  loadTpvs: (pageNum: number, filters?: TpvFilters) => Promise<void>
  downloadTpvsDataExcel: () => Promise<void>
  filters: TpvFilters
  setFilters: React.Dispatch<React.SetStateAction<TpvFilters>>
}

export const useTpvController = (
  repository: TpvsRepository,
  id: number,
): IUseTpvController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()

  const [model, setModel] = useState<TpvModel[]>([])
  const [count, setCount] = useState<number>(0)

  const { handleMetaResponse } = useMetaResponseHandler()
  const [filters, setFilters] = useState<TpvFilters>({ multiFieldFileter: '', merchantTargetList: [] })

  const form = useForm<TpvForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onChange',
    defaultValues: {
      terminalMid: '',
      merchantTargetId: id,
      merchantTargetList: [],
      multiFieldFileter: '',
    },
  })

  const loadTpvs = async (pageNum: number, filters?: TpvFilters): Promise<void> => {
    startLoading()

    const response = await TpvsContract(repository).getTpvsData(id, pageNum, filters ?? { multiFieldFileter: '', merchantTargetList: [] })

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      setModel(response.data?.rows ?? [])
      setCount(response.data?.totalRows ?? 0)
    }
  }

  const addNewTpv = async (data: TpvForm): Promise<boolean> => {
    startLoading()

    const response = await TpvsContract(repository).addNewTpv({
      terminalMid: removeFirstZeroFromTPV(data.terminalMid),
      merchantTargetId: data.merchantTargetId,
    })

    stopLoading()

    const valid = handleMetaResponse(response?.meta, undefined, { notifySuccess: true, successMessage: t(storesTranslation.tpvs.add.success) })

    if (valid) {
      loadTpvs(1, filters)
    }

    return valid
  }

  const downloadTpvsDataExcel = async (): Promise<void> => {
    startLoading()
    const response = await TpvsContract(repository).getTpvsDataExcel(id, filters)
    if (response?.meta?.status === MetaStatusCodes.SUCCESS) {
      dataToExcel(response?.data?.file || '', response?.data?.fileName || 'terminales.xlsx')
    } else {
      handleMetaResponse(response?.meta, undefined, { notifySuccess: false })
    }
    stopLoading()
  }

  return {
    form,
    model,
    count,
    addNewTpv,
    loadTpvs,
    downloadTpvsDataExcel,
    filters,
    setFilters,
  }
}
